import { useRef, useState } from "react";
import { ICellRendererParams, RowNode } from "ag-grid-community";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { AgGrid, Button, Card, Flex, Grid, Text, type AgGridReact } from "@jsq/ds/core";
import { Colors, Sizes } from "@jsq/ds/styles";
import { globalEnv } from "~/global-env";
import {
  ComplianceCaseStatus,
  ComplianceCaseTypeEnum,
  ComplianceObjectType,
} from "~/graphql/supergraphEnums";
import { useReconcileProfileHierarchyMutation } from "~/graphql/supergraphHooks";
import {
  type GetComplianceCaseQuery,
  type ProfileHierarchyRowInput,
} from "~/graphql/supergraphTypes";
import { ExtendedInfoTooltip } from "~/pages/Compliance/Common/ExtendedInfoTooltip";
import { AGGridCellRenderParams } from "~/pages/Compliance/ComplianceTable/types";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { useProfileHierarchy } from "~/pages/Compliance/context/ProfileHierarchyContext";
import {
  useComplianceEntityOnboardingEnabled,
  useGuidedProfileCreation,
} from "~/pages/Compliance/flags";
import { RiskRatingRenderer } from "~/pages/Compliance/riskRating/RiskRatingRenderer";
import {
  isIndividualAccountType,
  isJointAccountType,
} from "~/pages/Compliance/utils/accountTypeUtils";
import { ESCALATION_REVAMP_CASE_TYPES } from "../../../utils/constants";
import CreateEscalationModal from "../../Escalations/CreateEscalationModal";
import { escalationTakeoverPaths } from "../../Escalations/EscalationTakeover/paths";
import { ScreeningCellRenderer } from "../../PaymentInstructions/BeneficiarySummary/ScreeningCellRenderer";
import { PepExposureValue } from "../../pepExposure/PepExposureValue";
import { ScreeningStatusRenderer } from "../../Screening/ScreeningStatusRenderer";
import { ScreeningRecord } from "../../Screening/types";
import { ProfileActions } from "../common/ProfileActions";
import { DomicileRenderer } from "../DomicileRenderer";
import { ComplianceCase, OwnersAndControllersRowItem, ProfileCellRenderParams } from "../types";
import { OwnershipCellRenderer } from "./OwnershipCellRenderer";
import { OwnershipPercentCellEditor } from "./OwnershipPercentCellEditor";
import { OwnershipValidationBanner } from "./OwnershipValidationBanner";
import { ProfileCellRenderer } from "./ProfileCellRenderer";
import { RoleCellEditor } from "./RoleCellEditor";
import * as styles from "./styles_vanilla.css";
import { useProfileTreeData } from "./useProfileTreeData";
import {
  buildOwnershipValidationFromError,
  getFirstGraphQLError,
  isOwnerRole,
  parseOwnershipToNumber,
  type OwnershipValidationResult,
} from "./utils";

interface ProfilesTreeCardProps {
  complianceCase: ComplianceCase;
  caseId: number;
  readonly: boolean;
  programId?: number;
  showNestedOwnership?: boolean;
  showScreeningColumns?: boolean;
  fullComplianceCase?: GetComplianceCaseQuery["complianceCase"];
}

export const ProfilesTreeCard = ({
  complianceCase,
  caseId,
  readonly,
  programId,
  showNestedOwnership = false,
  showScreeningColumns = false,
  fullComplianceCase,
}: ProfilesTreeCardProps) => {
  const history = useHistory();
  const internalEscalationsEnabled = useComplianceEntityOnboardingEnabled();
  const [gridApi, setGridApi] = useState<AgGrid.GridApi<OwnersAndControllersRowItem> | null>(
    null,
  );
  const [isEscalationModalOpen, setIsEscalationModalOpen] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState<number | undefined>();
  const { isEditingHierarchy: editingProfileHierarchy, setEditingHierarchy } =
    useProfileHierarchy();
  const { editHierarchySupported: enableHierarchyEditing } = useCurrentCaseContext();
  const guidedProfileCreation = useGuidedProfileCreation();
  const frozenRowDataRef = useRef<OwnersAndControllersRowItem[] | null>(null);
  const [ownershipValidationResult, setOwnershipValidationResult] =
    useState<OwnershipValidationResult | null>(null);

  const [reconcileHierarchy, { loading: isSaving }] = useReconcileProfileHierarchyMutation({
    refetchQueries: ["GetProfilesForCase", "GetComplianceCase", "GetPositions"],
    awaitRefetchQueries: true,
    onCompleted: () => {
      toast.success("Hierarchy saved successfully");
      frozenRowDataRef.current = null;
      setOwnershipValidationResult(null);
      setEditingHierarchy(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const caseType = (fullComplianceCase?.caseType ?? complianceCase?.caseType) as
    ComplianceCaseTypeEnum | undefined;
  const showEscalationIcon =
    !!caseType && ESCALATION_REVAMP_CASE_TYPES.includes(caseType) && !readonly;

  const rootAccountType = complianceCase?.investingProfile?.accountType ?? null;

  const isIndividualRoot =
    rootAccountType != null
      ? isIndividualAccountType(rootAccountType)
      : complianceCase?.investingProfile?.complianceObjectType ===
        ComplianceObjectType.Individual;
  const isJointRoot = isJointAccountType(rootAccountType);
  const hierarchyEligible = guidedProfileCreation ? !(isIndividualRoot || isJointRoot) : true;

  const handleCreateOwnersAndControllersClick = () => {
    if (guidedProfileCreation) {
      if (isJointRoot) {
        history.push(`${globalEnv.complianceRoute}/case/${caseId}/create-individual-owner`);
      } else {
        history.push(`${globalEnv.complianceRoute}/case/${caseId}/profiles/create`);
      }
    } else {
      history.push(`${globalEnv.complianceRoute}/case/${caseId}/create-owners-and-controllers`);
    }
  };

  const saveHierarchy = async () => {
    gridApi?.stopEditing(false);

    const hierarchyRows: ProfileHierarchyRowInput[] = [];
    gridApi?.forEachNode((node) => {
      if (node.data) {
        hierarchyRows.push({
          id: node.data.id,
          parentId: node.data.parentId,
          relationship: node.data.relationship,
          ownership: node.data.ownership === "--" ? undefined : node.data.ownership,
          name: node.data.name,
        });
      }
    });

    const result = await reconcileHierarchy({
      variables: {
        hierarchyRows,
        caseId,
      },
    });

    const rawErr =
      (result as { error?: unknown; errors?: unknown }).error ??
      (result as { errors?: unknown }).errors;
    const gqErr = getFirstGraphQLError(rawErr);
    const validation = buildOwnershipValidationFromError(gqErr);
    if (validation) {
      setOwnershipValidationResult(validation);
    } else if (gqErr?.message) {
      toast.error(gqErr.message);
    }
  };

  const cancelEditingHierarchy = () => {
    frozenRowDataRef.current = null;
    setOwnershipValidationResult(null);
    setEditingHierarchy(false);
  };

  const handleEscalationIconClick = (profileId: number) => {
    const escalationCaseId = fullComplianceCase?.id ?? complianceCase?.id;
    if (internalEscalationsEnabled && escalationCaseId) {
      history.push(escalationTakeoverPaths.create(escalationCaseId, profileId), {
        from: history.location.pathname,
      });
      return;
    }
    setSelectedProfileId(profileId);
    setIsEscalationModalOpen(true);
  };

  const handleCloseEscalationModal = () => {
    setIsEscalationModalOpen(false);
    setSelectedProfileId(undefined);
  };

  const { rowData, baseColumnDefs, gridOptions } = useProfileTreeData({
    complianceCase,
    caseId,
    programId,
    readonly,
    showNestedOwnership,
    showScreeningColumns,
    enableRowDrag: enableHierarchyEditing && editingProfileHierarchy,
  });

  // When in editing mode, use frozen data to prevent re-renders from resetting the grid
  // The grid maintains its own state during editing via applyTransaction
  const gridRowData =
    editingProfileHierarchy && frozenRowDataRef.current ? frozenRowDataRef.current : rowData;

  const editHierarchy = () => {
    frozenRowDataRef.current = [...rowData];
    setEditingHierarchy(true);
    setOwnershipValidationResult(null);
  };

  const handleGridReady = (event: { api: AgGrid.GridApi<OwnersAndControllersRowItem> }) => {
    setGridApi(event.api);
  };

  type TypedAGGridCellRenderParams = AGGridCellRenderParams & {
    data: OwnersAndControllersRowItem;
    node: RowNode<OwnersAndControllersRowItem>;
  };

  const getOwnershipCellStyle = (
    params: { data?: OwnersAndControllersRowItem },
    validation: OwnershipValidationResult | null,
  ) => {
    const base = {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
    } as const;
    const id = params.data?.id;
    const isError = id && validation?.errorProfileIds.has(id);
    if (isError) {
      return {
        ...base,
        backgroundColor: Colors.Danger200,
        border: `1px solid ${Colors.Danger600}`,
      };
    }
    return base;
  };

  const cellRenderers = {
    ownership: (
      params: AGGridCellRenderParams & { validationResult?: OwnershipValidationResult },
    ) => (
      <OwnershipCellRenderer {...params} validationResult={params.validationResult ?? null} />
    ),
    domicile: (params: AGGridCellRenderParams) => <DomicileRenderer params={params} />,
    pepExposure: (params: AgGrid.ICellRendererParams<OwnersAndControllersRowItem>) => (
      <PepExposureValue pepCategory={params.data?.pepExposure} />
    ),
    riskRating: (params: AgGrid.ICellRendererParams<OwnersAndControllersRowItem>) => {
      const renderer = (
        <RiskRatingRenderer
          riskRating={params.data?.riskRating}
          disabled={editingProfileHierarchy}
        />
      );
      if (editingProfileHierarchy) {
        return (
          <ExtendedInfoTooltip
            message="This value will be recalculated after you save your changes."
            inverted
          >
            {renderer}
          </ExtendedInfoTooltip>
        );
      }
      return renderer;
    },
    profileNameRenderer: ({ data }: { data: OwnersAndControllersRowItem }) => (
      <ProfileCellRenderer
        data={data}
        onEscalationIconClick={handleEscalationIconClick}
        showEscalationIcon={showEscalationIcon}
      />
    ),
    screeningRecord: (params: TypedAGGridCellRenderParams) => {
      const rowData = params.data;
      const screeningParams: { value: ScreeningRecord | null } = {
        value: rowData?.screeningRecord || null,
      };
      return (
        <Flex
          alignItems="center"
          justifyContent="center"
          className={styles.screeningCellContainer}
        >
          <ScreeningStatusRenderer params={screeningParams} type="ScreeningStatus" />
        </Flex>
      );
    },
    screening: (params: AGGridCellRenderParams) => {
      const rowData = params.data as OwnersAndControllersRowItem;
      if (!rowData?.screening?.screeningRecord) {
        return "";
      }
      const screeningParams = {
        ...params,
        value: rowData.screening,
      } as ICellRendererParams;
      return <ScreeningCellRenderer {...screeningParams} />;
    },
    actions: (params: TypedAGGridCellRenderParams) => {
      const rowData = params.data;
      const profileParams: ProfileCellRenderParams = {
        value: {
          id: rowData.actions?.id || 0,
          name: rowData.actions?.name || "",
          isPaymentBeneficiary: rowData.actions?.isPaymentBeneficiary || false,
        },
        node: params.node,
        rowIndex: params.rowIndex,
      };
      const actionsComponent = (
        <ProfileActions
          params={profileParams}
          caseId={caseId}
          readonly={readonly}
          showDelete={!rowData?.isInvestorRoot}
          allowChildCreation={!rowData?.isInvestorRoot && showNestedOwnership}
          accountType={rowData?.accountType}
          complianceObjectType={rowData?.complianceObjectType}
          disabled={editingProfileHierarchy}
        />
      );
      if (editingProfileHierarchy) {
        return (
          <ExtendedInfoTooltip message="Actions are disabled in edit hierarchy mode">
            {actionsComponent}
          </ExtendedInfoTooltip>
        );
      }
      return actionsComponent;
    },
  };

  const columnDefs: AgGridReact.AgGridReactProps["columnDefs"] = baseColumnDefs.map((col) => {
    if (col.field === "relationship") {
      return {
        ...col,
        editable: (params: { data?: OwnersAndControllersRowItem }) =>
          editingProfileHierarchy && !params.data?.isInvestorRoot,
        cellEditor: RoleCellEditor,
        cellEditorPopup: true,
      };
    }
    if (col.field === "ownership") {
      return {
        ...col,
        editable: (params: { data?: OwnersAndControllersRowItem }) =>
          editingProfileHierarchy && isOwnerRole(params.data?.relationship),
        cellEditor: OwnershipPercentCellEditor,
        valueGetter: (params: { data?: OwnersAndControllersRowItem }) =>
          parseOwnershipToNumber(params.data?.ownership),
        valueSetter: (params: {
          data?: OwnersAndControllersRowItem;
          newValue?: number | null;
        }): boolean => {
          if (params.data) {
            const n = params.newValue;
            params.data.ownership = n != null && Number.isFinite(n) ? `${n}%` : "--";
          }
          return true;
        },
        cellRenderer: cellRenderers.ownership,
        cellRendererParams: { validationResult: ownershipValidationResult },
        cellStyle: (params: { data?: OwnersAndControllersRowItem }) =>
          getOwnershipCellStyle(params, ownershipValidationResult),
      };
    }
    const renderer = cellRenderers[col.field as keyof typeof cellRenderers];
    return renderer ? { ...col, cellRenderer: renderer } : col;
  });

  const hasSingleProfileOnly = rowData.length <= 1;

  const isEditHierarchyDisabled = readonly || hasSingleProfileOnly;

  return (
    <Card data-testid="profiles-container">
      <Flex
        className={styles.headerContainer}
        alignContent="center"
        alignItems="center"
        justifyContent="space-between"
      >
        <Text variant="secondary-md">PROFILES</Text>
        <Flex
          className={styles.headerContainer}
          alignContent="center"
          alignItems="center"
          gap={Sizes.MD}
          justifyContent="space-between"
        >
          {enableHierarchyEditing &&
            hierarchyEligible &&
            (editingProfileHierarchy ? (
              <>
                <Button onClick={cancelEditingHierarchy} variant="neutral" disabled={isSaving}>
                  Cancel
                </Button>
                <Button
                  onClick={() => void saveHierarchy()}
                  variant="primary"
                  icon="grip-horizontal"
                  isLoading={isSaving}
                >
                  Save changes
                </Button>
              </>
            ) : isEditHierarchyDisabled ? (
              <Button onClick={editHierarchy} variant="neutral" icon="grip-horizontal" disabled>
                Edit hierarchy
              </Button>
            ) : (
              <ExtendedInfoTooltip message="Edit the hierarchy of owners, controllers and payment beneficiaries associated with this investing profile by reordering and establishing parent and child data structure.">
                <Button onClick={editHierarchy} variant="neutral" icon="grip-horizontal">
                  Edit hierarchy
                </Button>
              </ExtendedInfoTooltip>
            ))}
          {isIndividualRoot && guidedProfileCreation ? (
            <ExtendedInfoTooltip message="Individual account type profiles do not allow child profiles.">
              <Button disabled variant="neutral" icon="plus">
                Create new
              </Button>
            </ExtendedInfoTooltip>
          ) : (
            <Button
              disabled={readonly || editingProfileHierarchy}
              onClick={handleCreateOwnersAndControllersClick}
              variant="neutral"
              icon="plus"
              className={editingProfileHierarchy ? styles.dimmed : undefined}
            >
              Create new
            </Button>
          )}
        </Flex>
      </Flex>
      {editingProfileHierarchy && (
        <OwnershipValidationBanner
          show={!!ownershipValidationResult?.message}
          message={ownershipValidationResult?.message ?? ""}
        />
      )}
      <Grid
        className={styles.profilesGrid}
        key={editingProfileHierarchy ? "editing" : "viewing"}
        gridOptions={gridOptions}
        rowData={gridRowData}
        columnDefs={columnDefs}
        components={cellRenderers}
        onGridReady={handleGridReady}
      />
      {isEscalationModalOpen && (
        <CreateEscalationModal
          show={isEscalationModalOpen}
          onCloseModal={handleCloseEscalationModal}
          caseType={ComplianceCaseTypeEnum.InvestorOnboarding}
          profileId={selectedProfileId ?? complianceCase?.investingProfile?.id ?? 0}
          caseStatus={ComplianceCaseStatus.Ready}
          profilesForCase={fullComplianceCase}
          data-testid="create-escalation-modal"
        />
      )}
    </Card>
  );
};
